/// <amd-module name="Wit/QueriesHub/WorkItemEdit/WorkItemEditComponent" />
import * as React from "react";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { IReadyableObservableArray, ObservableValue } from "VSS/Core/Observable";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { IPerformanceScenarioPendingWork, PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { Components, IVssComponentContext } from "VSS/Platform/Layout";
import { Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IQueryService, QueryHubView } from "Wit/Common/QueryService/IQueryService";
import { reportError } from "Wit/Common/QueryUtils/ErrorHandlingUtils";
import * as QueriesHubConstants from "Wit/Common/QueryUtils/QueriesHubConstants";
import { QueryHeader } from "Wit/QueriesHub/Common/Components/QueryHeader";
import { IQueryResultItem } from "Wit/QueriesHub/Common/Interfaces";
import { WorkItemFormView } from "Wit/WorkItemForm/WorkItemFormView";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";

interface IWorkItemEditComponentState {
    loading: boolean;
    workItem?: IWorkItem;
}

export class WorkItemEditComponent extends TfsComponent<{}, IWorkItemEditComponentState> {
    private queryResultItems: IReadyableObservableArray<IQueryResultItem>;
    private lastFocusedRow = new ObservableValue<number>(0);
    private queryService: IQueryService;
    private workItemId: number;
    private pendingWork: IPerformanceScenarioPendingWork | undefined;

    constructor(props: any, context: IVssComponentContext) {
        super(props, context);
        this.queryService = context.pageContext.getService<IQueryService>("IQueryService");

        if (this.queryService.previousView === QueryHubView.QueryResults) {
            this.queryResultItems = this.queryService.filteredQueryResultItems;
        } else {
            this.queryResultItems = this.queryService.queryResultItems;
        }

        this.state = { loading: true };
    }

    public async componentDidMount() {
        await this.ExecuteQueryIfInQueryResultsEditMode();
        this.queryService.addResultListeners();
        await this.loadWorkItem();
        this.pendingWork?.complete();

        if (!this.queryService.query && this.projectData?.id) {
            this.queryService.getQuery(this.projectData.id);
        }
    }

    private async ExecuteQueryIfInQueryResultsEditMode() {
        if (
            !this.queryService.previousView &&
            this.queryService.view.value === QueryHubView.WorkItemEdit &&
            (!this.queryResultItems || this.queryResultItems.length == 0)
        ) {
            const wiql = await this.queryService.generateWiql(this.projectData?.id ?? "");
            await this.queryService.executeQuery(this.projectData?.id ?? "", wiql ?? "", true);
            this.queryResultItems = this.queryService.queryResultItems;
        }
    }

    private async loadWorkItem(workItemId?: number) {
        if (workItemId) {
            this.workItemId = workItemId;
        } else {
            const pageStateHistoryService = this.context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
            const state = pageStateHistoryService.getProjectedState();
            this.workItemId = parseInt(state["id"]);
        }

        this.lastFocusedRow.value = this.queryResultItems.value.findIndex(item => item.value[CoreFieldRefNames.Id] === this.workItemId);
        this.queryService.lastEditedWorkItem = this.workItemId;

        let workItem = this.queryService.workItems.value.find(item => this.workItemId === item.id);
        if (!workItem) {
            try {
                const isDeleted = this.queryService.isRecycleBin;
                const workItemModelService = this.context.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
                workItem = await workItemModelService.getWorkItem(this.workItemId, { isDeleted, includeHistory: false });
                this.queryService.workItems.value.push(workItem);
            } catch (e) {
                if (e?.message) {
                    reportError(this.context.pageContext, "WorkItemEditComponent", e);
                }
            }
        }

        this.setState({ workItem, loading: false });
    }

    public componentWillUnmount() {
        this.queryService.removeResultListeners();
    }

    public render(): JSX.Element {
        const { loading, workItem } = this.state;
        return (
            <PerformanceScenarioContext.Consumer>
                {scenarioProps => {
                    if (!this.pendingWork) {
                        this.pendingWork = scenarioProps.registerPendingWork(QueriesHubConstants.WorkItemEditViewed);
                    }

                    return (
                        <Surface background={SurfaceBackground.normal}>
                            <Page className="flex-grow flex-column">
                                <QueryHeader
                                    queryResultItems={this.queryResultItems}
                                    lastFocusedRow={this.lastFocusedRow}
                                    onFocusNext={this.onFocusNext}
                                    onFocusPrevious={this.onFocusPrevious}
                                />
                                {loading ? (
                                    <Spinner className="flex-grow" size={SpinnerSize.large} />
                                ) : (
                                    // WorkItemFormView doesn't handle changing the workItem very well.
                                    // By keying on the work item id, we hack^w work around that.
                                    workItem && (
                                        <WorkItemFormView
                                            workItem={workItem}
                                            key={workItem.id}
                                            onDelete={() => this.onDeleteItem(workItem.id)}
                                            updateDocumentTitle
                                        />
                                    )
                                )}
                            </Page>
                        </Surface>
                    );
                }}
            </PerformanceScenarioContext.Consumer>
        );
    }

    private onFocusNext = () => {
        this.focusItem(this.queryResultItems.value[this.lastFocusedRow.value + 1].value[CoreFieldRefNames.Id]);
    };

    private onFocusPrevious = () => {
        this.focusItem(this.queryResultItems.value[this.lastFocusedRow.value - 1].value[CoreFieldRefNames.Id]);
    };

    private focusItem(workItemId: number) {
        const pageStateHistoryService = this.context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        pageStateHistoryService.queueValueChange("id", String(workItemId), HistoryBehavior.replace);
        this.loadWorkItem(workItemId);
    }

    private onDeleteItem(workItemId: number) {
        this.queryService.removeWorkItems(new Set([workItemId]));

        const indexToFocus =
            this.lastFocusedRow.value === this.queryService.queryResultItems.value.length
                ? this.lastFocusedRow.value - 1
                : this.lastFocusedRow.value + 1;
        this.focusItem(this.queryResultItems.value[indexToFocus].value[CoreFieldRefNames.Id]);
    }
}

Components.add("query-edit-work-item-component", WorkItemEditComponent);
